"use client";

import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { MapPin, DollarSign, Clock } from "lucide-react";
import type { JobListing } from "@/types";
import { Badge } from "@/components/ui/badge";

interface JobCardProps {
    job: JobListing;
}

export default function JobCard({ job }: JobCardProps) {
    const router = useRouter();

    const handleJobClick = () => {
        router.push(`/careers/${job.id}`);
    };

    return (
        <motion.div
            className="bg-white rounded-xl border cursor-pointer group hover:shadow-lg hover:shadow-primary-100"
            onClick={handleJobClick}
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
        >
            <div className="p-4 lg:p-6">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                            {job.title}
                        </h3>
                        <p className="text-sm lg:text-base text-gray-600 mt-1">{job.description}</p>
                    </div>
                    <Badge variant='gradient'>
                        {job.type}
                    </Badge>
                </div>
    
                <div className="flex flex-wrap gap-2 lg:gap-4 text-xs lg:text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.location}
                    </span>
                    <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {job.salary}
                    </span>
                    <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {job.experience}
                    </span>
                </div>
    
                <div className="mt-4 flex flex-wrap gap-2">
                    {job.requirements.slice(0, 3).map((req, index) => (
                        <Badge
                            key={index}
                            variant="outline-gradient"
                            className="text-xs lg:text-sm"
                        >
                            {req}
                        </Badge>
                    ))}
                    {job.requirements.length > 3 && (
                        <Badge
                            variant="outline-gradient"
                            className="text-xs lg:text-sm"
                        >
                            +{job.requirements.length - 3} more
                        </Badge>
                    )}
                </div>
            </div>
        </motion.div>
    )
}